import React, { useState, useEffect } from 'react';
import { Smartphone, RotateCw } from 'lucide-react';
import { Capacitor } from '@capacitor/core';

/**
 * PortraitLock Component
 * Restricts the app to portrait mode on phone devices in PWA and Native environments.
 * Allows tablets to use the app in any orientation.
 */
const PortraitLock = () => {
    const [isLocked, setIsLocked] = useState(false);

    useEffect(() => {
        const checkOrientation = () => {
            // 1. Detect environment: We only lock for PWA (standalone) or Native apps.
            const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
                || (window.navigator && window.navigator.standalone);
            const isNative = Capacitor.isNativePlatform();

            if (!isStandalone && !isNative) {
                setIsLocked(false);
                return;
            }

            // 2. Detect orientation & device type
            const isLandscape = window.innerWidth > window.innerHeight;
            
            // Shortest dimension of the physical screen:
            // Using window.screen.width/height is stable and does not change when the virtual keyboard pops up.
            // Falls back to window.innerWidth/innerHeight if window.screen is unavailable.
            const screenWidth = (window.screen && window.screen.width) || window.innerWidth;
            const screenHeight = (window.screen && window.screen.height) || window.innerHeight;
            const shortestDim = Math.min(screenWidth, screenHeight);

            // - Phones (iPhone Pro Max, etc) are typically < 500px.
            // - Tablets (iPad Mini, etc) are typically >= 768px.
            // 600px is a safe threshold to distinguish phones from tablets.
            const isPhone = shortestDim < 600;

            // 3. Lock only if Phone + Landscape
            setIsLocked(isLandscape && isPhone);
        };

        // iOS Viewport Reset Logic:
        // Occasionally, iOS PWA mode 'zooms' the viewport when rotating to landscape 
        // and doesn't reset it when rotating back to portrait.
        const resetViewportScale = () => {
            // Force scroll to top to reset any 'spill'
            window.scrollTo(0, 0);
            
            // Re-assert viewport content to trigger a layout/scale recalculation
            const viewport = document.querySelector('meta[name="viewport"]');
            if (viewport) {
                const content = viewport.getAttribute('content');
                // Temporarily append a microscopic change to force a repaint/re-scale
                viewport.setAttribute('content', content + ', initial-scale=1.0001');
                setTimeout(() => {
                    viewport.setAttribute('content', content);
                }, 100);
            }
        };

        // Listen for resize/orientation change
        const handleLayoutChange = () => {
            checkOrientation();
            resetViewportScale();
        };

        window.addEventListener('resize', handleLayoutChange);
        window.addEventListener('orientationchange', handleLayoutChange);
        
        // Initial check
        checkOrientation();
        resetViewportScale();

        return () => {
            window.removeEventListener('resize', handleLayoutChange);
            window.removeEventListener('orientationchange', handleLayoutChange);
        };
    }, []);

    if (!isLocked) return null;

    return (
        <div className="fixed inset-0 z-[99999] bg-[#0d1f1a] flex flex-col items-center justify-center p-8 text-center animate-in fade-in duration-500 overflow-hidden" style={{ touchAction: 'none' }}>
            {/* Background Decorative Elements - themed after Budlee splash */}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(186,240,0,0.05)_0%,transparent_70%)] pointer-events-none" />
            
            <div className="relative">
                {/* Visual Icon with rotation animation */}
                <div className="relative w-24 h-24 mb-8">
                    <div className="absolute inset-0 bg-[hsl(var(--primary))]/20 rounded-full animate-ping" />
                    <div className="relative w-full h-full bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(var(--teal-vivid))] rounded-full flex items-center justify-center shadow-xl">
                        <RotateCw className="w-12 h-12 text-white animate-spin" style={{ animationDuration: '3s' }} />
                    </div>
                </div>
                
                {/* Mascot Icon Fallback or Smartphone indicator */}
                <Smartphone className="absolute -bottom-2 -right-2 w-8 h-8 text-white/40 rotate-12" />
            </div>

            <h2 className="text-2xl font-black text-white mb-4 tracking-tight">
                Please Rotate Your Device
            </h2>
            
            <p className="text-[hsl(var(--primary))]/80 text-lg font-medium max-w-[280px] leading-relaxed">
                Budlee AI works best in <span className="text-white font-bold">Portrait Mode</span> on phones.
            </p>

            <div className="mt-12 opacity-50">
                <div className="w-16 h-1 bg-white/20 rounded-full mx-auto" />
            </div>
        </div>
    );
};

export default PortraitLock;
